/**
 * Protected Route Component
 * Redirects to login if not authenticated
 * Includes safeguards against infinite redirect loops
 */

import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/useAuthStore'
import { LoadingSpinner } from './ui/loading-spinner'
import { useEffect, useRef } from 'react'

interface ProtectedRouteProps {
  children: React.ReactNode
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, _hasHydrated } = useAuthStore()
  const location = useLocation()
  const redirectCountRef = useRef(0)
  const lastRedirectTimeRef = useRef(0)
  const hasRedirectedRef = useRef(false)

  // Reset redirect count after 10 seconds (increased from 5)
  useEffect(() => {
    const now = Date.now()
    if (now - lastRedirectTimeRef.current > 10000) {
      redirectCountRef.current = 0
      hasRedirectedRef.current = false
    }
  }, [location])

  // Wait for store to hydrate before making any decisions
  if (!_hasHydrated || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!isAuthenticated) {
    // Prevent infinite redirect loops with more robust detection
    const now = Date.now()
    const timeSinceLastRedirect = now - lastRedirectTimeRef.current
    
    // If we've already redirected in the last 3 seconds, don't redirect again
    if (hasRedirectedRef.current && timeSinceLastRedirect < 3000) {
      console.error('[ProtectedRoute] Prevented rapid redirect loop')
      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-2">Authentication Error</h2>
            <p className="text-muted-foreground mb-4">
              Unable to verify authentication. Please clear your browser cache and try again.
            </p>
            <button
              onClick={() => {
                // Clear all storage and redirect to login
                localStorage.clear()
                sessionStorage.clear()
                window.location.href = '/login'
              }}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md"
            >
              Clear Cache & Login
            </button>
          </div>
        </div>
      )
    }
    
    // Track redirect attempts within a longer window
    if (timeSinceLastRedirect < 5000) {
      redirectCountRef.current++
      if (redirectCountRef.current > 2) {
        console.error('[ProtectedRoute] Detected redirect loop (multiple attempts)')
        return (
          <div className="min-h-screen flex items-center justify-center p-4">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-2">Authentication Error</h2>
              <p className="text-muted-foreground mb-4">
                Multiple authentication attempts detected. Please clear your browser cache.
              </p>
              <button
                onClick={() => {
                  localStorage.clear()
                  sessionStorage.clear()
                  window.location.href = '/login'
                }}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md"
              >
                Clear Cache & Login
              </button>
            </div>
          </div>
        )
      }
    } else {
      redirectCountRef.current = 1
    }
    
    lastRedirectTimeRef.current = now
    hasRedirectedRef.current = true

    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <>{children}</>
}
